<script>
import {FilterMatchMode} from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import Drawer from "primevue/drawer";
import Textarea from "primevue/textarea";
import Dialog from "primevue/dialog";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import DashboardLayout from "../layouts/DashboardLayout.vue";
import StateBlock from "../components/StateBlock.vue";
import {api} from "../services/api";

export default {
    name: "LeadsView",
    components: {
        DashboardLayout,
        StateBlock,
        DataTable,
        Column,
        InputText,
        Drawer,
        Textarea,
        Dialog,
        VueSelect,
    },
    data() {
        return {
            leads: [],
            loading: true,
            detailsOpen: false,
            detailsLoading: false,
            detailsError: "",
            selectedLead: null,
            leadDetails: null,
            activeSession: null,
            sessionModalOpen: false,
            actionOutcome: null,
            actionChannel: null,
            actionDate: new Date().toISOString().slice(0, 16),
            actionStatus: "hot",
            actionNotes: "",
            contactChannelOptions: [
                {label: "Not Specified", value: null},
                {label: "Phone", value: "phone"},
                {label: "Email", value: "email"},
                {label: "WhatsApp", value: "whatsapp"},
                {label: "In Person", value: "inperson"},
            ],
            filters: {
                name: {value: null, matchMode: FilterMatchMode.CONTAINS},
                email: {value: null, matchMode: FilterMatchMode.CONTAINS},
                phone: {value: null, matchMode: FilterMatchMode.CONTAINS},
                preferredCountry: {value: null, matchMode: FilterMatchMode.CONTAINS},
                programType: {value: null, matchMode: FilterMatchMode.CONTAINS},
                startDate: {value: null, matchMode: FilterMatchMode.CONTAINS},
                createdAt: {value: null, matchMode: FilterMatchMode.CONTAINS},
            },
        };
    },
    computed: {
        InfoSession() {
            return this.leadDetails?.informationSession || null;
        },
        LeadInitials() {
            const name = this.leadDetails?.lead?.name || this.selectedLead?.name;
            if (!name) return "?";
            return name
                .split(" ")
                .map((w) => w[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
        },
        LeadCountry() {
            return this.InfoSession?.geolocationCountry || this.selectedLead?.preferredCountry || null;
        },
        LeadHotScore() {
            return this.leadDetails?.lead?.hotScore ?? null;
        },
        LeadScore() {
            const lead = this.leadDetails?.lead || this.selectedLead || {};
            return this.CalculateAIScore(lead);
        },
        LeadScoreColor() {
            const s = this.LeadScore;
            if (s >= 75) return "#ef4444";
            if (s >= 50) return "#f97316";
            return "#3b82f6";
        },
        LeadProgram() {
            return this.leadDetails?.lead?.programType || this.selectedLead?.programType || null;
        },
        LeadStartDate() {
            return this.leadDetails?.lead?.startDate || this.selectedLead?.startDate || null;
        },
        InformationItems() {
            const lead = this.leadDetails?.lead || {};
            const session = this.InfoSession || {};
            return [
                {label: "Email", value: lead.email || "-"},
                {label: "Phone", value: lead.phone || "-"},
                {
                    label: "Country",
                    value: this.JoinValues(session.geolocationCountry, session.geolocationCountryCode),
                    flag: session.geolocationCountryFlag,
                    emoji: session.geolocationCountryEmoji,
                },
                {label: "City", value: this.JoinValues(session.geolocationCity, session.geolocationState)},
                {label: "District", value: session.geolocationDistrict || "-"},
                {label: "Zipcode", value: session.geolocationZipcode || "-"},
                {
                    label: "Timezone",
                    value: this.JoinValues(session.geolocationTimezone, session.geolocationTimezoneOffset != null ? `UTC ${session.geolocationTimezoneOffset}` : ""),
                },
                {
                    label: "Currency",
                    value: this.JoinValues(session.geolocationCurrencyCode, session.geolocationCurrencyName, session.geolocationCurrencySymbol),
                },
                {label: "Calling code", value: session.geolocationCallingCode || "-"},
                {label: "TLD", value: session.geolocationTld || "-"},
                {label: "Browser language", value: session.browserLanguage || "-"},
            ];
        },
        DeviceInfo() {
            const session = this.InfoSession || {};
            return this.ParseUserAgent(session.userAgent);
        },
        BrowserItems() {
            const session = this.InfoSession || {};
            return [
                {label: "IP", value: session.ip || "-", emoji: "🌐"},
                {label: "Page URL", value: session.pageUrl || "-", emoji: "🔗"},
                {label: "Page title", value: session.pageTitle || "-", emoji: "📄"},
                {label: "First session", value: session.startedAt ? this.FormatDate(session.startedAt) : "-", emoji: "🕐"},
                {label: "Last message", value: session.lastMessageAt ? this.FormatDate(session.lastMessageAt) : "-", emoji: "💬"},
            ];
        },
        ActivityTimeline() {
            const items = [];
            if (this.leadDetails?.sessions?.length) {
                for (const session of this.leadDetails.sessions) {
                    items.push({
                        color: "#3b82f6",
                        title: "AI Agent Chat Completed",
                        date: session.lastMessageAt || session.startedAt,
                        description: session.summary || "Chat session completed.",
                    });
                }
            }
            if (this.leadDetails?.lead?.createdAt) {
                items.push({
                    color: "#22c55e",
                    title: "Lead Created",
                    date: this.leadDetails.lead.createdAt,
                    description: "Captured via Website Widget",
                });
            }
            return items.sort((a, b) => new Date(b.date) - new Date(a.date));
        },
    },
    methods: {
        ParseUserAgent(ua) {
            if (!ua) return {device: "-", deviceIcon: "PhMonitor", os: "-", osIcon: "PhMonitor", browser: "-", browserIcon: "PhGlobe"};
            let device = "Desktop",
                deviceIcon = "PhMonitor";
            if (/iPhone/.test(ua)) {
                device = "iPhone";
                deviceIcon = "PhDeviceMobile";
            } else if (/iPad/.test(ua)) {
                device = "iPad";
                deviceIcon = "PhDeviceTablet";
            } else if (/Android/.test(ua) && /Mobile/.test(ua)) {
                device = "Android Phone";
                deviceIcon = "PhDeviceMobile";
            } else if (/Android/.test(ua)) {
                device = "Android Tablet";
                deviceIcon = "PhDeviceTablet";
            } else if (/Macintosh/.test(ua)) {
                device = "Mac";
                deviceIcon = "PhMonitor";
            } else if (/Windows/.test(ua)) {
                device = "PC";
                deviceIcon = "PhMonitor";
            } else if (/Linux/.test(ua)) {
                device = "Linux Device";
                deviceIcon = "PhMonitor";
            }
            let os = "-",
                osIcon = "PhMonitor";
            let m;
            if ((m = ua.match(/Mac OS X ([\d_]+)/))) {
                os = "macOS " + m[1].replace(/_/g, ".");
                osIcon = "PhAppleLogo";
            } else if ((m = ua.match(/Windows NT ([\d.]+)/))) {
                const map = {"10.0": "10/11", 6.3: "8.1", 6.2: "8", 6.1: "7", "6.0": "Vista"};
                os = "Windows " + (map[m[1]] || m[1]);
                osIcon = "PhWindowsLogo";
            } else if ((m = ua.match(/Android ([\d.]+)/))) {
                os = "Android " + m[1];
                osIcon = "PhAndroidLogo";
            } else if ((m = ua.match(/iPhone OS ([\d_]+)/))) {
                os = "iOS " + m[1].replace(/_/g, ".");
                osIcon = "PhAppleLogo";
            } else if ((m = ua.match(/iPad.*OS ([\d_]+)/))) {
                os = "iPadOS " + m[1].replace(/_/g, ".");
                osIcon = "PhAppleLogo";
            } else if (/Linux/.test(ua)) {
                os = "Linux";
                osIcon = "PhTerminal";
            }
            let browser = "-",
                browserIcon = "PhGlobe";
            if ((m = ua.match(/Edg\/([\d.]+)/))) {
                browser = "Edge " + m[1].split(".")[0];
                browserIcon = "PhGlobe";
            } else if ((m = ua.match(/OPR\/([\d.]+)/))) {
                browser = "Opera " + m[1].split(".")[0];
                browserIcon = "PhGlobe";
            } else if ((m = ua.match(/Firefox\/([\d.]+)/))) {
                browser = "Firefox " + m[1].split(".")[0];
                browserIcon = "PhGlobe";
            } else if ((m = ua.match(/Chrome\/([\d.]+)/))) {
                browser = "Chrome " + m[1].split(".")[0];
                browserIcon = "PhGlobe";
            } else if ((m = ua.match(/Version\/([\d.]+).*Safari/))) {
                browser = "Safari " + m[1].split(".")[0];
                browserIcon = "PhGlobe";
            } else if (/Safari/.test(ua)) {
                browser = "Safari";
                browserIcon = "PhGlobe";
            }
            return {device, deviceIcon, os, osIcon, browser, browserIcon};
        },
        FormatDate(value) {
            if (!value) return "-";
            return new Date(value).toLocaleString();
        },
        JoinValues(...values) {
            const result = values.filter((v) => v !== null && v !== undefined && String(v).trim() !== "").join(" / ");
            return result || "-";
        },
        async LoadLeads() {
            try {
                this.leads = (await api.get("/leads")).data;
            } finally {
                this.loading = false;
            }
        },
        async DeleteLead(id) {
            await api.delete(`/leads/${id}`);
            this.leads = this.leads.filter((lead) => lead.id !== id);
            if (this.selectedLead?.id === id) {
                this.CloseDetails();
            }
        },
        async OpenDetails(lead) {
            this.selectedLead = lead;
            this.detailsOpen = true;
            this.detailsLoading = true;
            this.detailsError = "";
            this.leadDetails = null;
            this.actionOutcome = null;
            this.actionChannel = null;
            this.actionDate = new Date().toISOString().slice(0, 16);
            this.actionStatus = "hot";
            this.actionNotes = "";
            try {
                this.leadDetails = (await api.get(`/leads/${lead.id}`)).data;
            } catch (error) {
                this.detailsError = error?.response?.data?.error || "Lead details could not be loaded.";
            } finally {
                this.detailsLoading = false;
            }
        },
        CloseDetails() {
            this.detailsOpen = false;
            this.selectedLead = null;
            this.leadDetails = null;
            this.detailsError = "";
            this.activeSession = null;
        },
        OpenSession(session) {
            this.activeSession = session;
            this.sessionModalOpen = true;
        },
        CloseSession() {
            this.sessionModalOpen = false;
            this.activeSession = null;
        },
        isSenderRole(role) {
            const r = String(role || "").toLowerCase();
            return r === "user" || r === "visitor" || r === "human";
        },
        roleAvatarUrl(message) {
            const r = String(message?.role || "").toLowerCase();
            if (this.isSenderRole(r)) return "/assets/images/human/user.webp";
            return "/assets/images/human/female/female-1.png";
        },
        CalculateAIScore(lead) {
            let score = 0;
            if (lead.name) score += 5;
            if (lead.email) score += 20;
            if (lead.phone) score += 20;
            if (lead.programType) score += 25;
            if (lead.startDate) score += 20;
            if (lead.preferredCountries?.length) score += 10;
            return score;
        },
        ScoreColor(score) {
            if (score >= 75) return "#ef4444";
            if (score >= 50) return "#f97316";
            return "#3b82f6";
        },
    },
    async mounted() {
        await this.LoadLeads();
    },
};
</script>
<template>
    <DashboardLayout title="Leads" subtitle="Review enquiries captured by your AI Agents.">
        <section class="panel">
            <StateBlock v-if="!loading && leads.length === 0" title="No leads yet" message="Captured leads will appear here." />
            <DataTable
                v-else
                v-model:filters="filters"
                :value="leads"
                class="table"
                paginator
                :rows="10"
                :rowsPerPageOptions="[5, 10, 20, 50]"
                :loading="loading"
                filterDisplay="row"
                sortField="createdAt"
                :sortOrder="-1"
            >
                <Column field="name" header="AI Score" sortable>
                    <template #body="{data}">
                        <div style="position: relative; width: 40px; height: 40px; flex-shrink: 0">
                            <svg viewBox="0 0 40 40" width="40" height="40" style="transform: rotate(-90deg); position: absolute; top: 0; left: 0">
                                <circle cx="20" cy="20" r="16" fill="none" stroke="#e2e8f0" stroke-width="3" />
                                <circle
                                    cx="20"
                                    cy="20"
                                    r="16"
                                    fill="none"
                                    :stroke="ScoreColor(CalculateAIScore(data))"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-dasharray="100.53"
                                    :stroke-dashoffset="String(100.53 * (1 - CalculateAIScore(data) / 100))"
                                />
                            </svg>
                            <span
                                :style="{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: '9px',
                                    fontWeight: '700',
                                    color: ScoreColor(CalculateAIScore(data)),
                                    lineHeight: '1',
                                    whiteSpace: 'nowrap',
                                }"
                                >{{ CalculateAIScore(data) }}</span
                            >
                        </div>
                    </template>
                </Column>
                <Column field="name" header="Name" sortable :showFilterMenu="false">
                    <template #body="{data}">
                        <a role="button" @click="OpenDetails(data)" style="color: #2563eb; cursor: pointer">
                            {{ data.name || "Unknown" }}
                            <PhArrowSquareOut :size="14" />
                        </a>
                    </template>
                    <template #filter="{filterModel, filterCallback}">
                        <InputText v-model="filterModel.value" placeholder="Filter name" @input="filterCallback()" />
                    </template>
                </Column>

                <Column field="email" header="Email" sortable :showFilterMenu="false">
                    <template #body="{data}">{{ data.email || "-" }}</template>
                    <template #filter="{filterModel, filterCallback}">
                        <InputText v-model="filterModel.value" placeholder="Filter email" @input="filterCallback()" />
                    </template>
                </Column>

                <Column field="phone" header="Phone" sortable :showFilterMenu="false">
                    <template #body="{data}">{{ data.phone || "-" }}</template>
                    <template #filter="{filterModel, filterCallback}">
                        <InputText v-model="filterModel.value" placeholder="Filter phone" @input="filterCallback()" />
                    </template>
                </Column>

                <Column field="preferredCountry" header="Country" sortable :showFilterMenu="false">
                    <template #body="{data}">{{ data.preferredCountry || "-" }}</template>
                    <template #filter="{filterModel, filterCallback}">
                        <InputText v-model="filterModel.value" placeholder="Filter country" @input="filterCallback()" />
                    </template>
                </Column>

                <Column field="programType" header="Program" sortable :showFilterMenu="false">
                    <template #body="{data}">{{ data.programType || "-" }}</template>
                    <template #filter="{filterModel, filterCallback}">
                        <InputText v-model="filterModel.value" placeholder="Filter program" @input="filterCallback()" />
                    </template>
                </Column>

                <Column field="startDate" header="Start" sortable :showFilterMenu="false">
                    <template #body="{data}">{{ data.startDate || "-" }}</template>
                    <template #filter="{filterModel, filterCallback}">
                        <InputText v-model="filterModel.value" placeholder="Filter start" @input="filterCallback()" />
                    </template>
                </Column>

                <Column field="createdAt" header="Created" sortable :showFilterMenu="false">
                    <template #body="{data}">{{ FormatDate(data.createdAt) }}</template>
                    <template #filter="{filterModel, filterCallback}">
                        <InputText v-model="filterModel.value" placeholder="Filter created" @input="filterCallback()" />
                    </template>
                </Column>

                <Column header="" :showFilterMenu="false" :showClearButton="false" :showApplyButton="false">
                    <template #body="{data}">
                        <div class="lead-actions">
                            <button class="ghost-button" @click="DeleteLead(data.id)">Delete</button>
                        </div>
                    </template>
                </Column>
            </DataTable>
        </section>

        <!-- Lead Details Drawer -->
        <Drawer
            v-model:visible="detailsOpen"
            position="right"
            :style="{width: 'min(560px, 100vw)'}"
            :pt:header:style="'padding: 0;'"
            :pt:content:style="'padding-top: 1rem;background-color: #f8fafc;'"
            :pt:closeButton:style="'position: absolute; right: 1rem; top: 1rem; z-index: 1;'"
            class="drawer-modern"
        >
            <template #header>
                <div style="display: flex; flex-direction: column; width: 100%">
                    <div class="ld-header">
                        <div class="ld-avatar-wrap">
                            <svg class="ld-avatar-ring" viewBox="0 0 110 110">
                                <circle class="ld-avatar-ring-bg" cx="55" cy="55" r="50" />
                                <circle
                                    class="ld-avatar-ring-fill"
                                    cx="55"
                                    cy="55"
                                    r="50"
                                    :style="{
                                        strokeDasharray: '314.16',
                                        strokeDashoffset: String(314.16 * (1 - LeadScore / 100)),
                                        stroke: LeadScoreColor,
                                    }"
                                />
                            </svg>
                            <div class="ld-avatar">{{ LeadInitials }}</div>
                            <span class="ld-score-badge" :style="{background: LeadScoreColor}">{{ LeadScore }}</span>
                        </div>
                        <div class="ld-identity">
                            <h2 class="ld-name">{{ selectedLead?.name || "Unknown" }}</h2>
                            <div class="ld-badges">
                                <span v-if="LeadHotScore != null" class="badge badge-hot">Hot: {{ LeadHotScore }}</span>
                                <span v-if="LeadProgram" class="badge badge-program">{{ LeadProgram }}</span>
                                <span v-if="LeadStartDate" class="badge badge-date">{{ LeadStartDate }}</span>
                                <span v-if="LeadCountry" class="ld-country">
                                    <span v-if="InfoSession?.geolocationCountryEmoji">{{ InfoSession.geolocationCountryEmoji }}</span>
                                    {{ LeadCountry }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Contact Action Buttons -->
                    <div class="ld-contact-actions">
                        <a v-if="selectedLead?.phone" :href="`tel:${selectedLead.phone}`" class="contact-btn">
                            <span class="contact-icon">
                                <PhPhone :size="20" weight="fill" />
                            </span>
                            Call
                        </a>
                        <a v-if="selectedLead?.email" :href="`mailto:${selectedLead.email}`" class="contact-btn">
                            <span class="contact-icon">
                                <PhEnvelope :size="20" weight="fill" />
                            </span>
                            Email
                        </a>
                        <a
                            v-if="selectedLead?.phone"
                            :href="`https://wa.me/${selectedLead.phone.replace(/\D/g, '')}`"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="contact-btn contact-btn-whatsapp"
                        >
                            <span class="contact-icon">
                                <PhWhatsappLogo :size="20" weight="fill" />
                            </span>
                            WhatsApp
                        </a>
                    </div>
                </div>
            </template>
            <div class="ld-wrap">
                <div v-if="detailsLoading" class="ld-loading">Loading details…</div>
                <div v-else-if="detailsError" class="error-banner">{{ detailsError }}</div>
                <template v-else>
                    <!-- New - Action Required -->
                    <div class="action-card">
                        <div class="action-card-header"><PhBell :size="18" weight="fill" /> New - Action Required</div>
                        <div class="action-card-body">
                            <div class="action-row">
                                <label class="action-label">Contact Outcome:</label>
                                <div class="outcome-buttons">
                                    <button class="outcome-btn" :class="{active: actionOutcome === 'contacted'}" @click="actionOutcome = 'contacted'">
                                        <PhCheck :size="14" weight="bold" /> Contacted
                                    </button>
                                    <button class="outcome-btn" :class="{active: actionOutcome === 'cantreach'}" @click="actionOutcome = 'cantreach'">
                                        <PhX :size="14" weight="bold" /> Couldn't reach
                                    </button>
                                    <button class="outcome-btn" :class="{active: actionOutcome === 'noncontact'}" @click="actionOutcome = 'noncontact'">
                                        <PhProhibit :size="14" weight="bold" /> Non-contact Action
                                    </button>
                                </div>
                            </div>

                            <div class="action-row action-row-inline">
                                <div class="action-field">
                                    <label class="action-label">Contact Channel: <span class="required">*</span></label>
                                    <VueSelect v-model="actionChannel" :options="contactChannelOptions" :reduce="(opt) => opt.value" label="label" :clearable="false" class="action-select" />
                                </div>
                                <div class="action-field">
                                    <label class="action-label">Date:</label>
                                    <input v-model="actionDate" type="datetime-local" class="action-input" />
                                </div>
                            </div>

                            <div class="action-row">
                                <label class="action-label">Lead Status:</label>
                                <div class="status-buttons">
                                    <button class="status-btn cold" :class="{active: actionStatus === 'cold'}" @click="actionStatus = 'cold'">
                                        <PhSnowflake :size="18" weight="fill" class="status-icon" />
                                        <span class="status-label">Cold</span>
                                        <span class="status-desc">Not Interested</span>
                                    </button>
                                    <button class="status-btn warm" :class="{active: actionStatus === 'warm'}" @click="actionStatus = 'warm'">
                                        <PhHandshake :size="18" weight="fill" class="status-icon" />
                                        <span class="status-label">Warm</span>
                                        <span class="status-desc">Showing Interest</span>
                                    </button>
                                    <button class="status-btn hot" :class="{active: actionStatus === 'hot'}" @click="actionStatus = 'hot'">
                                        <PhFire :size="18" weight="fill" class="status-icon" />
                                        <span class="status-label">Hot</span>
                                        <span class="status-desc">Ready to convert</span>
                                    </button>
                                    <button class="status-btn superhot" :class="{active: actionStatus === 'superhot'}" @click="actionStatus = 'superhot'">
                                        <PhFire :size="18" weight="fill" class="status-icon" />
                                        <span class="status-label">Super Hot</span>
                                        <span class="status-desc">High priority</span>
                                    </button>
                                </div>
                            </div>

                            <div class="action-row">
                                <label class="action-label">Note &amp; Comments:</label>
                                <Textarea v-model="actionNotes" rows="3" placeholder="Add your notes here…" class="action-textarea" autoResize />
                            </div>
                        </div>
                    </div>

                    <!-- Information -->
                    <div class="ld-section">
                        <div class="ld-section-title"><PhIdentificationCard :size="16" weight="bold" /> INFORMATION</div>
                        <dl class="detail-list">
                            <template v-for="item in InformationItems" :key="item.label">
                                <dt>{{ item.label }}</dt>
                                <dd>
                                    <template v-if="item.flag">
                                        <img :src="item.flag" alt="" class="flag-img" />
                                        {{ item.value }}
                                    </template>
                                    <template v-else-if="item.emoji"> {{ item.emoji }} {{ item.value }} </template>
                                    <template v-else>{{ item.value }}</template>
                                </dd>
                            </template>
                        </dl>
                    </div>

                    <!-- AI Conversation Summary -->
                    <div v-if="leadDetails?.sessions?.length" class="ai-session-list">
                        <div class="ai-summary-card">
                            <div class="ai-summary-card-header">
                                <PhRobot :size="14" weight="fill" />
                                <span>AI CONVERSATION SUMMARY</span>
                            </div>
                            <p class="ai-summary-text">{{ leadDetails.conversationSummary }}</p>

                            <div style="padding: 1rem; padding-top: 0">
                                <span style="font-size: 0.8rem; color: #555; font-weight: 500; display: block; margin-bottom: 0.5rem">
                                    INCLUDING {{ leadDetails.sessions.length }} CHAT SESSION{{ leadDetails.sessions.length > 1 ? "S" : "" }}
                                </span>

                                <template v-for="(session, sIndex) in leadDetails.sessions" :key="session.id">
                                    <a role="button" class="ai-session-card" @click="OpenSession(session)">
                                        <PhChatCircleDots :size="14" weight="fill" />
                                        <span style="flex-grow: 1">Chat session #{{ sIndex + 1 }}</span>
                                        <small class="ai-session-text">{{ FormatDate(session.startedAt) }}</small>
                                    </a>
                                </template>
                            </div>
                        </div>
                    </div>

                    <!-- Activity Timeline -->
                    <div v-if="ActivityTimeline.length" class="ld-section">
                        <div class="ld-section-title"><PhArrowsLeftRight :size="16" weight="bold" /> ACTIVITY TIMELINE</div>
                        <div class="timeline">
                            <div v-for="(item, idx) in ActivityTimeline" :key="idx" class="timeline-item">
                                <div class="timeline-dot" :style="{background: item.color}"></div>
                                <div class="timeline-body">
                                    <div class="timeline-title">
                                        {{ item.title }}
                                        <span class="timeline-date">{{ FormatDate(item.date) }}</span>
                                    </div>
                                    <div class="timeline-desc">{{ item.description }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Technical Details -->
                    <div class="ld-section">
                        <div class="ld-section-title"><PhMonitor :size="16" weight="bold" /> TECHNICAL DETAILS</div>
                        <div class="device-chips">
                            <div class="device-chip">
                                <PhMonitor v-if="DeviceInfo.deviceIcon === 'PhMonitor'" :size="20" weight="fill" class="device-chip-icon" />
                                <PhDeviceMobile v-else-if="DeviceInfo.deviceIcon === 'PhDeviceMobile'" :size="20" weight="fill" class="device-chip-icon" />
                                <PhDeviceTablet v-else-if="DeviceInfo.deviceIcon === 'PhDeviceTablet'" :size="20" weight="fill" class="device-chip-icon" />
                                <div class="device-chip-text">
                                    <span class="device-chip-label">Device</span>
                                    <span class="device-chip-value">{{ DeviceInfo.device }}</span>
                                </div>
                            </div>
                            <div class="device-chip">
                                <PhAppleLogo v-if="DeviceInfo.osIcon === 'PhAppleLogo'" :size="20" weight="fill" class="device-chip-icon" />
                                <PhWindowsLogo v-else-if="DeviceInfo.osIcon === 'PhWindowsLogo'" :size="20" weight="fill" class="device-chip-icon" />
                                <PhAndroidLogo v-else-if="DeviceInfo.osIcon === 'PhAndroidLogo'" :size="20" weight="fill" class="device-chip-icon" />
                                <PhTerminal v-else :size="20" weight="fill" class="device-chip-icon" />
                                <div class="device-chip-text">
                                    <span class="device-chip-label">OS</span>
                                    <span class="device-chip-value">{{ DeviceInfo.os }}</span>
                                </div>
                            </div>
                            <div class="device-chip">
                                <PhGlobe :size="20" weight="fill" class="device-chip-icon" />
                                <div class="device-chip-text">
                                    <span class="device-chip-label">Browser</span>
                                    <span class="device-chip-value">{{ DeviceInfo.browser }}</span>
                                </div>
                            </div>
                        </div>
                        <dl class="detail-list" style="margin-top: 14px">
                            <template v-for="item in BrowserItems" :key="item.label">
                                <dt>{{ item.label }}</dt>
                                <dd>
                                    {{ item.value }}
                                </dd>
                            </template>
                        </dl>
                    </div>
                </template>
            </div>
        </Drawer>

        <!-- Chat Session Dialog -->
        <Dialog
            v-model:visible="sessionModalOpen"
            modal
            :header="activeSession ? `Chat session – ${FormatDate(activeSession.startedAt)}` : 'Chat session'"
            :style="{width: 'min(720px, calc(100vw - 32px))'}"
            :pt="{content: {style: 'padding: 0; background: var(--bg-surface-soft); max-height: min(600px, calc(100vh - 180px)); overflow-y: auto;'}}"
            @hide="CloseSession"
        >
            <div class="chat-thread">
                <div v-for="message in activeSession?.messages" :key="message.id" class="chat-message" :class="{sender: isSenderRole(message.role)}">
                    <div class="chat-message-avatar">
                        <img :src="roleAvatarUrl(message)" :alt="message.role" />
                    </div>
                    <div class="chat-message-data">
                        <div class="chat-message-data-inner">
                            <div class="message-text">
                                <span class="msg-badge">{{ message.role }}</span>
                                <div v-html="message.content"></div>
                                <div v-if="message.usedSkills?.length" class="message-skills">
                                    <span style="display: block; font-weight: bold; margin-bottom: 0.25rem">Used Skills:</span>
                                    <span v-for="skill in message.usedSkills" :key="skill.id" class="skill-badge">{{ skill.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="!activeSession?.messages?.length" class="chat-empty">No messages in this session.</div>
            </div>
        </Dialog>
    </DashboardLayout>
</template>

<style scoped>
/* ── Table ───────────────────────────────────────────── */
.lead-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
}

/* ── Drawer wrap ─────────────────────────────────────── */
.ld-wrap {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-height: 100%;
}

/* ── Lead header ─────────────────────────────────────── */
.ld-header {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 20px 20px 16px;
    border-bottom: 1px solid var(--border-subtle);
}

.ld-avatar-wrap {
    position: relative;
    width: 110px;
    height: 110px;
    flex-shrink: 0;
}

.ld-avatar {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: #6366f1;
    color: #fff;
    font-weight: 700;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ld-avatar-ring {
    position: absolute;
    top: 0;
    left: 0;
    width: 110px;
    height: 110px;
    transform: rotate(-90deg);
}

.ld-avatar-ring-bg {
    fill: none;
    stroke: #e2e8f0;
    stroke-width: 5;
}

.ld-avatar-ring-fill {
    fill: none;
    stroke-width: 5;
    stroke-linecap: round;
    transition: stroke-dashoffset 0.6s ease;
}

.ld-score-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 26px;
    height: 26px;
    border-radius: 999px;
    color: #fff;
    font-size: 0.72rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border: 2px solid #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.ld-identity {
    flex: 1;
    min-width: 0;
}

.ld-name {
    margin: 0 0 6px;
    font-size: 1.05rem;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ld-badges {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
}

.badge-hot {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.78rem;
    font-weight: 600;
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
}

.badge-program {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.78rem;
    font-weight: 600;
    background: #f0fdf4;
    color: #15803d;
    border: 1px solid #86efac;
}

.badge-date {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.78rem;
    font-weight: 600;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
}

.ld-country {
    font-size: 0.83rem;
    color: var(--text-secondary);
}

.ld-close {
    flex-shrink: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-secondary);
    font-size: 1rem;
    padding: 2px 4px;
    line-height: 1;
}

/* ── Contact action buttons ──────────────────────────── */
.ld-contact-actions {
    display: flex;
    gap: 10px;
    padding: 14px 20px;
}

.contact-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 9px 12px;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    background: var(--bg-surface);
    color: var(--text-primary);
    font-size: 0.88rem;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s;
}

.contact-btn:hover {
    background: var(--bg-surface-soft);
}

.contact-btn-whatsapp {
    background: #f0fdf4;
    border-color: #86efac;
    color: #15803d;
}

.contact-btn-whatsapp:hover {
    background: #dcfce7;
}

.ld-loading {
    padding: 24px 20px;
    color: var(--text-secondary);
}

/* ── Sections ────────────────────────────────────────── */
.ld-section {
    padding: 18px 20px;
    border: 1px solid var(--border-subtle);
    border-radius: 1rem;
    background-color: #fff;
    margin-top: 0.5rem;
}

.ld-section-title {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: var(--text-secondary);
    text-transform: uppercase;
    margin-bottom: 14px;
}

/* ── Action required card ────────────────────────────── */
.action-card {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #ef4443;
    padding: unset;
}

.action-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 16px;
    background: #ef4443;
    color: #fff;
    font-size: 0.92rem;
    font-weight: 600;
}

.action-card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    background: var(--bg-surface);
}

.action-label {
    display: block;
    font-size: 0.82rem;
    color: var(--text-secondary);
    margin-bottom: 7px;
}

.required {
    color: #dc2626;
}

.action-row-inline {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.action-field {
    display: flex;
    flex-direction: column;
}

.outcome-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.outcome-btn {
    padding: 7px 12px;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    background: var(--bg-surface);
    color: var(--text-primary);
    font-size: 0.83rem;
    cursor: pointer;
    transition: all 0.15s;
}

.outcome-btn.active {
    border-color: #16a34a;
    background: #f0fdf4;
    color: #15803d;
    font-weight: 600;
}

.action-select {
    width: 100%;
}

.action-input {
    padding: 7px 10px;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    background: var(--bg-surface);
    color: var(--text-primary);
    font-size: 0.88rem;
    width: 100%;
    box-sizing: border-box;
}

.status-buttons {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.status-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 10px 4px;
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    background: var(--bg-surface);
    cursor: pointer;
    transition: all 0.15s;
    text-align: center;
}

.status-btn .status-label {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--text-primary);
}

.status-btn .status-desc {
    font-size: 0.72rem;
    color: var(--text-secondary);
    line-height: 1.2;
}

.status-btn.hot.active {
    border-color: #f97316;
    background: #fff7ed;
}

.status-btn.hot.active .status-label {
    color: #ea580c;
}

.status-btn.superhot.active {
    border-color: #dc2626;
    background: #fef2f2;
}

.status-btn.superhot.active .status-label {
    color: #dc2626;
}

.status-btn.warm.active {
    border-color: #d97706;
    background: #fffbeb;
}

.status-btn.warm.active .status-label {
    color: #b45309;
}

.status-btn.cold.active,
.status-btn.cold .status-icon {
    color: #0284c7;
}

.status-btn.warm .status-icon {
    color: #d97706;
}

.status-btn.hot .status-icon {
    color: #f97316;
}

.status-btn.superhot .status-icon {
    color: #dc2626;
}

.status-btn.cold.active {
    border-color: #0284c7;
    background: #f0f9ff;
}

.status-btn.cold.active .status-label {
    color: #0369a1;
}

.action-textarea {
    width: 100%;
    resize: vertical;
}

/* ── Detail list ─────────────────────────────────────── */
.detail-list {
    display: grid;
    grid-template-columns: minmax(110px, 0.38fr) 1fr;
    gap: 10px 14px;
    margin: 0;
}

.detail-list dt {
    color: var(--text-secondary);
    font-size: 0.82rem;
}

.detail-list dd {
    margin: 0;
    color: var(--text-primary);
    overflow-wrap: anywhere;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.88rem;
}

.flag-img {
    width: 18px;
    height: 18px;
    border-radius: 2px;
    object-fit: cover;
}

.detail-emoji {
    font-size: 0.95rem;
    line-height: 1;
    flex-shrink: 0;
}

/* ── Device chips ────────────────────────────────────── */
.device-chips {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 4px;
}

.device-chip {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 10px 12px;
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    background: var(--bg-surface-soft, #f8fafc);
}

.device-chip-icon {
    color: #1c3654;
    flex-shrink: 0;
}

.device-chip-text {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
}

.device-chip-label {
    font-size: 0.7rem;
    color: var(--text-secondary);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.device-chip-value {
    font-size: 0.83rem;
    font-weight: 600;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ── AI Summary Cards ────────────────────────────────── */
.ai-session-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.ai-summary-card {
    background: linear-gradient(336deg, #ffffff 0%, #ffffff 60%, #a8c7fb40 100%);
    border: 1px solid #ddd6fe;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(99, 102, 241, 0.08);
    transition:
        box-shadow 0.15s,
        border-color 0.15s,
        transform 0.1s;
    overflow: hidden;
    margin-top: 0.5rem;
}

.ai-summary-card-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px 8px;
    border-bottom: 1px solid var(--border-subtle);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: var(--text-secondary);
    text-transform: uppercase;
}

.ai-summary-arrow {
    margin-left: auto;
    color: #94a3b8;
}

.ai-summary-text {
    margin: 0;
    padding: 12px 14px 10px;
    color: var(--text-primary);
    line-height: 1.6;
    font-size: 0.875rem;
    white-space: pre-wrap;
}

.ai-summary-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 14px 12px;
}

.ai-summary-date {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.78rem;
    color: var(--text-secondary);
}

.intent-badge {
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 0.78rem;
    font-weight: 600;
    background: #f0fdf4;
    color: #15803d;
    border: 1px solid #86efac;
}

/* ── Timeline ────────────────────────────────────────── */
.timeline {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.timeline-item {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

.timeline-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 4px;
}

.timeline-body {
    flex: 1;
}

.timeline-title {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    font-size: 0.88rem;
    font-weight: 600;
    color: var(--text-primary);
    flex-wrap: wrap;
}

.timeline-date {
    font-size: 0.78rem;
    font-weight: 400;
    color: var(--text-secondary);
    white-space: nowrap;
}

.timeline-desc {
    margin-top: 3px;
    font-size: 0.82rem;
    color: var(--text-secondary);
}

/* ── Chat history (inside Dialog) ───────────────────── */
.chat-thread {
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.chat-empty {
    padding: 24px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 0.88rem;
}

.chat-message {
    display: flex;
    gap: 10px;
    align-items: flex-end;
}

.chat-message.sender {
    flex-direction: row-reverse;
}

.chat-message-avatar {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid var(--border-subtle);
}

.chat-message-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.chat-message-data {
    max-width: 75%;
}

.chat-message-data-inner .message-text {
    padding: 10px 14px;
    border-radius: 14px 14px 14px 4px;
    background: #f1f5f9;
    color: #1e293b;
    font-size: 0.875rem;
    line-height: 1.55;
}

.chat-message .chat-message-data-inner .message-text {
    background-color: #ededed;
    border-radius: 14px 14px 14px 4px;
}

.chat-message.sender .chat-message-data-inner .message-text {
    background: #145eee;
    color: #fff;
    border-radius: 14px 14px 4px 14px;
}

.msg-badge {
    display: inline-block;
    margin-bottom: 5px;
    padding: 2px 8px;
    border-radius: 8px;
    font-size: 0.72rem;
    font-weight: 700;
    background: rgba(0, 0, 0, 0.12);
    color: inherit;
    letter-spacing: 0.03em;
}

.message-skills {
    margin-top: 8px;
    font-size: 0.82rem;
}

.skill-badge {
    display: inline-block;
    margin: 2px 4px 2px 0;
    padding: 2px 9px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    font-size: 0.75rem;
    font-weight: 600;
}

/* ── Error banner ────────────────────────────────────── */
.error-banner {
    margin: 20px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid color-mix(in srgb, #ef4444 45%, transparent);
    background: color-mix(in srgb, #ef4444 12%, transparent);
    color: #b91c1c;
    font-size: 0.9rem;
}

@media (max-width: 640px) {
    .detail-list {
        grid-template-columns: 1fr;
        gap: 4px;
    }

    .lead-actions {
        flex-direction: column;
    }

    .action-row-inline {
        grid-template-columns: 1fr;
    }

    .status-buttons {
        grid-template-columns: repeat(2, 1fr);
    }
}
.ai-session-card {
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    padding: 12px 14px;
    background: var(--bg-surface);
    cursor: pointer;
    transition: background 0.15s;
    display: flex;
    align-items: center;
    gap: 1rem;
    justify-content: space-between;
    font-size: 0.8rem;
    margin-bottom: 8px;
}
.ai-session-card:hover {
    border-color: #a5b4fc;
    box-shadow: 0 6px 18px rgba(99, 102, 241, 0.15);
    transform: translateY(-1px);
}
</style>
